import * as React from "react"
import PropTypes from "prop-types"
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority";

import { cn } from "@/lib/utils"
import LoadingSpinner from "@/components/common/LoadingSpinner/LoadingSpinner";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-0 focus:outline-none focus:ring-0 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-[#70AE42] text-white shadow hover:bg-[#4C7A2E] hover:shadow-md active:scale-[0.98]",
        destructive:
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90 hover:shadow-md active:scale-[0.98]",
        outline:
          "border border-gray-300 bg-white text-gray-700 shadow-sm hover:bg-gray-50 hover:border-gray-400 hover:text-gray-900 hover:shadow-md active:scale-[0.98] focus:outline-none focus:ring-0",
        secondary:
          "bg-secondary border border-transparent text-secondary-foreground shadow-sm hover:bg-secondary/80 hover:text-secondary-foreground hover:shadow-md active:scale-[0.98]",
        ghost: "hover:bg-gray-100 hover:text-gray-900",
        link: "text-primary underline-offset-4 hover:underline",
        primary:
          "bg-[#70AE42] text-white shadow hover:bg-[#4C7A2E] hover:shadow-md active:scale-[0.98]",
        error:
          "bg-[#D9534F] text-white shadow-sm hover:bg-[#B43E3A] hover:shadow-md active:scale-[0.98]",
        success:
          "bg-[#2196F3] text-white shadow-sm hover:bg-[#1976D2] hover:shadow-md active:scale-[0.98]",
        warning:
          "bg-[#FFC107] text-black shadow-sm hover:bg-[#FFA000] hover:shadow-md active:scale-[0.98]",
        info:
          "bg-[#2196F3] text-white shadow-sm hover:bg-[#1976D2] hover:shadow-md active:scale-[0.98]",
        "customer-destructive":
          "border border-red-300 bg-white text-red-600 shadow-sm hover:bg-red-50 hover:border-red-400 hover:text-red-700 hover:shadow-md focus-visible:ring-0 focus-visible:ring-offset-0 active:bg-red-100 active:border-red-500 active:scale-[0.98]",
        "dezshop-success":
          "border border-green-600 bg-white text-green-600 shadow-sm hover:bg-green-50 hover:border-green-700 hover:text-green-700 hover:shadow-md focus-visible:ring-0 focus-visible:ring-offset-0 active:bg-green-100 active:border-green-700 active:scale-[0.98]",
        light:
          "bg-gray-100 text-gray-700 hover:bg-gray-200 hover:shadow-md active:bg-gray-200 active:scale-[0.98]",
        dark:
          "bg-gray-700 text-white hover:bg-gray-800 hover:shadow-md active:bg-gray-800 active:scale-[0.98]",
        icon:
          "bg-transparent text-gray-700 hover:bg-gray-100 hover:text-gray-900 active:bg-gray-200 p-1",
        "upload-dotted":
          "border-2 border-dashed border-[#70AE42] bg-gray-100 text-[#4C7A2E] shadow-sm hover:bg-gray-200 hover:border-[#4C7A2E] hover:text-[#4C7A2E] hover:shadow-md active:scale-[0.98]",
        kran:
          "bg-kran-primary text-black shadow hover:bg-kran-primary-dark hover:text-black hover:shadow-md active:scale-[0.98]",
        "kran-secondary":
          "bg-gray-900 text-white shadow-sm hover:bg-black hover:shadow-md active:scale-[0.98]",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-10 rounded-md px-8",
        icon: "h-9 w-9",
        regular: "h-9 px-4 py-2",
        small: "h-8 rounded-md px-3 text-xs",
        md: "h-9 px-4 py-2",
        xs: "h-[26px] px-[6px] py-[4px] text-xs leading-[1.2]", // For SkuTable actions
        xxs: "h-[22px] px-[5px] py-[2px] text-[11px] leading-[1.2] [&_svg]:size-3", // Ultra compact
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

const Button = React.forwardRef(({ 
  className, 
  variant, 
  size, 
  asChild = false, 
  iconLeft,
  iconRight,
  isLoading = false,
  disabled = false,
  children,
  dataAction, // For special action button styles (scrap, waste, return)
  ...props 
}, ref) => {
  const Comp = asChild ? Slot : "button"
  
  // Special styling for specific actions
  let actionClasses = "";
  if (dataAction === "waste" || dataAction === "scrap") {
    actionClasses = "!bg-[#D9534F] !text-white !border-0 hover:!bg-[#B43E3A] disabled:!bg-gray-300 focus:!outline-none focus:!ring-0";
  } else if (dataAction === "return") {
    actionClasses = "!bg-[#2196F3] !text-white !border-0 hover:!bg-[#1976D2] focus:!outline-none focus:!ring-0";
  }
  
  // Choose spinner color to match the button style
  const getSpinnerColorForVariant = (v) => {
    switch (v) {
      case "default":
      case "primary":
      case "destructive":
      case "error":
      case "success":
      case "info":
      case "secondary":
      case "dark":
      case "kran-secondary":
        return "white";
      case "outline":
      case "ghost":
      case "link":
      case "light":
      case "icon":
      case "customer-destructive":
      case "dezshop-success":
      case "upload-dotted":
      case "kran":
        return "gray";
      case "warning":
        return "gray";
      default:
        return "white";
    }
  };
  const spinnerColor = getSpinnerColorForVariant(variant);
  
  return (
    <Comp
      className={cn(
        buttonVariants({ variant, size }),
        actionClasses,
        // Apply special styles for action buttons in SkuTable
        props['data-action'] && "min-h-[26px]",
        className
      )}
      ref={ref}
      disabled={disabled || isLoading}
      type={props.type !== undefined ? props.type : (props.onClick ? "button" : undefined)}
      data-action={dataAction || props['data-action']}
      {...props}
    >
      {asChild ? (
        // When composing with asChild, ensure a single child element without extra siblings
        children
      ) : (
        <>
          {isLoading && <LoadingSpinner size="xs" inline color={spinnerColor} />}
          {!isLoading && iconLeft && <span className="button-icon-left">{iconLeft}</span>}
          {children}
          {!isLoading && iconRight && <span className="button-icon-right">{iconRight}</span>}
        </>
      )}
    </Comp>
  );
})
Button.displayName = "Button"

Button.propTypes = {
  className: PropTypes.string,
  variant: PropTypes.oneOf([
    "default",
    "destructive",
    "outline",
    "secondary",
    "ghost",
    "link",
    "primary",
    "error",
    "success",
    "warning",
    "info",
    "customer-destructive",
    "dezshop-success",
    "light",
    "dark",
    "icon",
    "upload-dotted",
    "kran",
    "kran-secondary",
  ]),
  size: PropTypes.oneOf([
    "default",
    "sm",
    "lg",
    "icon",
    "regular",
    "small",
    "md",
    "xs",
    "xxs",
  ]),
  asChild: PropTypes.bool,
  iconLeft: PropTypes.node,
  iconRight: PropTypes.node,
  onClick: PropTypes.func,
  isLoading: PropTypes.bool,
  disabled: PropTypes.bool,
  type: PropTypes.oneOf(["button", "submit", "reset"]),
  children: PropTypes.node,
  dataAction: PropTypes.oneOf(["waste", "scrap", "return"]),
  // Accessed via props['data-action']
  ['data-action']: PropTypes.string,
}

export { Button }
